import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SelectMe from 'react-select-me';

import { formatCurrency } from '../../../../common-utils/formatters';
import type { User } from '../../../@types/User';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';
import AttributionMap, { MARKER_COLORS } from './AttributionMap';
import { computeSmartAttribution } from './smartAttribution';
import {
  buildEvenDistribution,
  filterTransactionnaires,
  renderUserLabel,
  sumPercentages,
} from './smartAttributionUtils';
import type {
  SmartAttributionCluster,
  SmartAttributionCoproInput,
  SmartAttributionCoproMarker,
  SmartAttributionDebugParams,
  SmartAttributionDialogProps,
  SmartAttributionManagerMarker,
  SmartAttributionResult,
  SmartAttributionSalesEntry,
  SmartAttributionTarget,
} from './types';
import {
  DEBUG_DEFAULTS,
  hasCoordinate,
  toTargetInput,
} from './types';

const FRANCE_CENTER = { lat: 46.603354, long: 1.888334 };
const MIN_SALES_THRESHOLD = 2;

type Step = 'config' | 'placement' | 'result';

const percentageFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

interface LocationApiResponse {
  id: number;
  lat?: number;
  long?: number;
  latitude?: number;
  longitude?: number;
  lng?: number;
  Lat?: number;
  Lng?: number;
  LAT?: number;
  LONG?: number;
}

const headers = [
  'Transactionnaire',
  'Objectif %',
  'Objectif CA',
  'Attribution CA',
  'Couverture vs cible',
  'Nombre de copros',
];

const renderClusterRow = (
  cluster: SmartAttributionCluster,
  userLookup: Record<number, User>,
  totalSales: number,
) => {
  const user = userLookup[cluster.userId];
  const pct = cluster.coveragePercentage;
  const coverageDiff = pct - cluster.targetPercentage;
  const diffText = coverageDiff === 0 ? '0%' : `${coverageDiff > 0 ? '+' : ''}${percentageFormatter.format(coverageDiff)}%`;

  return {
    Transactionnaire: user ? renderUserLabel(user) : 'Utilisateur inconnu',
    'Objectif %': `${cluster.targetPercentage}%`,
    'Objectif CA': formatCurrency(cluster.targetSalesAmount || 0),
    'Attribution CA': formatCurrency(cluster.assignedSalesAmount || 0),
    'Couverture vs cible': `${percentageFormatter.format(pct)}% (${diffText})`,
    'Nombre de copros': cluster.assignments.length,
    id: cluster.userId,
    rawAssignments: cluster.assignments,
    totalSales,
  };
};

const buildUserLookup = (users: User[]) => users.reduce<Record<number, User>>((acc, user) => {
  acc[user.id] = user;
  return acc;
}, {});

const hasLatLng = (copro: SmartAttributionCoproInput): copro is SmartAttributionCoproInput & Required<Pick<SmartAttributionCoproInput, 'lat' | 'long'>> =>
  copro.lat != null && copro.long != null && Number.isFinite(copro.lat) && Number.isFinite(copro.long);

const fetchLocations = async({
  companyId,
  jwt,
}: { companyId: number; jwt: string | null; }): Promise<LocationApiResponse[]> => {
  const response = await fetch(`/api/companies/${companyId}/copros/locations`, {
    headers: {
      Accept: 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
  });
  if (!response.ok) {
    throw new Error('failed-locations');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

const areTargetsValid = (targets: SmartAttributionTarget[]) => {
  if (targets.length === 0) {
    return false;
  }

  if (!targets.every((target) => Number.isFinite(target.percentage) && target.percentage > 0)) {
    return false;
  }

  const total = sumPercentages(targets);
  return Math.abs(total - 100) < 0.5;
};

const buildCoproInputs = (
  sales: SmartAttributionSalesEntry[],
  locations: LocationApiResponse[],
): SmartAttributionCoproInput[] => {
  const locationMap = locations.reduce<Record<number, LocationApiResponse>>((acc, location) => {
    acc[location.id] = location;
    return acc;
  }, {});

  return sales.map((sale) => {
    const location = locationMap[sale.id];
    const rawLat = location?.lat ?? location?.latitude ?? location?.Lat ?? location?.LAT ?? null;
    const rawLong = location?.long ?? location?.lng ?? location?.Lng ?? location?.longitude ?? location?.LONG ?? null;

    const lat = typeof rawLat === 'string' ? parseFloat(rawLat) : rawLat;
    const long = typeof rawLong === 'string' ? parseFloat(rawLong) : rawLong;

    const safeLat = Number.isFinite(lat) ? lat as number : null;
    const safeLong = Number.isFinite(long) ? long as number : null;

    return {
      id: sale.id,
      name: sale.name,
      salesAmount: Number(sale.salesAmount ?? 0),
      salesCount: sale.salesCount ?? null,
      lat: safeLat,
      long: safeLong,
    };
  });
};

const computeDefaultAnchors = (copros: SmartAttributionCoproInput[], count: number) => {
  if (count === 0) {
    return [];
  }

  const geolocated = copros.filter(hasLatLng);
  const base = geolocated.length > 0
    ? {
      lat: geolocated.reduce((acc, copro) => acc + copro.lat, 0) / geolocated.length,
      long: geolocated.reduce((acc, copro) => acc + copro.long, 0) / geolocated.length,
    }
    : FRANCE_CENTER;

  if (count === 1) {
    return [base];
  }

  const latitudes = geolocated.map((copro) => copro.lat);
  const longitudes = geolocated.map((copro) => copro.long);
  const latRange = latitudes.length > 0 ? Math.max(...latitudes) - Math.min(...latitudes) : 1;
  const longRange = longitudes.length > 0 ? Math.max(...longitudes) - Math.min(...longitudes) : 1;
  const radius = Math.max(0.05, Math.max(latRange, longRange) / 6);

  return Array.from({ length: count }, (_value, index) => ({
    lat: base.lat + (radius * Math.sin((2 * Math.PI * index) / count)),
    long: base.long + (radius * Math.cos((2 * Math.PI * index) / count)),
  }));
};

const SmartAttributionDialog = ({
  isOpen,
  onClose,
  companyUsers,
  companyId,
  jwt,
  salesEntries,
  totalCoprosCount,
  onApply,
}: SmartAttributionDialogProps) => {
  const [step, setStep] = useState<Step>('config');
  const [targets, setTargets] = useState<SmartAttributionTarget[]>([]);
  const [result, setResult] = useState<SmartAttributionResult | null>(null);
  const [availableTransactionnaires, setAvailableTransactionnaires] = useState<User[]>([]);
  const [copros, setCopros] = useState<SmartAttributionCoproInput[]>([]);
  const [excludeLowSales, setExcludeLowSales] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [computationError, setComputationError] = useState<string | null>(null);
  const [isLoadingCopros, setIsLoadingCopros] = useState(false);
  const [isComputing, setIsComputing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placementDirty, setPlacementDirty] = useState(false);
  const [highlightedTransactionnaire, setHighlightedTransactionnaire] = useState<number | null>(null);
  const [debugParams, setDebugParams] = useState<SmartAttributionDebugParams>({ ...DEBUG_DEFAULTS });

  const userLookup = useMemo(() => buildUserLookup(companyUsers), [companyUsers]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const transactionnaires = filterTransactionnaires(companyUsers);
    setAvailableTransactionnaires(transactionnaires);

    setTargets((currentTargets) => {
      const existingById = new Map(currentTargets.map((target) => [target.user.id, target]));
      const keepers = transactionnaires
        .map((user) => existingById.get(user.id))
        .filter((target): target is SmartAttributionTarget => Boolean(target));

      if (keepers.length > 0) {
        return keepers.map((target) => ({ ...target }));
      }

      const suggested = transactionnaires.slice(0, Math.min(transactionnaires.length, 3));
      return buildEvenDistribution(suggested);
    });

    setStep('config');
    setResult(null);
    setLoadError(null);
    setComputationError(null);
    setIsLoadingCopros(false);
    setIsComputing(false);
    setIsSubmitting(false);
    setPlacementDirty(false);
    setHighlightedTransactionnaire(null);
    setExcludeLowSales(false);
    setCopros([]);
  }, [isOpen, companyUsers]);

  const ensureCoprosLoaded = useCallback(async() => {
    if (!companyId) {
      setLoadError('Aucune entreprise sélectionnée.');
      console.warn('[SmartAttributionDialog] missing companyId, abort loading copros');
      return false;
    }

    if (!salesEntries || salesEntries.length === 0) {
      setLoadError('Aucune donnée de ventes disponible pour l’attribution.');
      console.warn('[SmartAttributionDialog] missing sales entries, abort loading copros');
      return false;
    }

    setIsLoadingCopros(true);
    setLoadError(null);

    try {
      const locations = await fetchLocations({ companyId, jwt });

      const coprosToAssign = buildCoproInputs(salesEntries, locations);
      setCopros(coprosToAssign);
      return true;
    } catch (error) {
      console.error('[SmartAttribution] load copros error', error);
      setLoadError('Impossible de charger les copropriétés géolocalisées. Veuillez réessayer.');
      return false;
    } finally {
      setIsLoadingCopros(false);
    }
  }, [companyId, jwt, salesEntries]);

  useEffect(() => {
    setCopros([]);
  }, [companyId, salesEntries]);

  useEffect(() => {
    if (copros.length === 0) {
      return;
    }

    setTargets((currentTargets) => {
      if (!currentTargets.some((target) => !hasCoordinate(target.anchor))) {
        return currentTargets;
      }

      const defaults = computeDefaultAnchors(copros, currentTargets.length);
      return currentTargets.map((target, index) => {
        if (hasCoordinate(target.anchor)) {
          return target;
        }
        const fallback = defaults[index] ?? FRANCE_CENTER;
        return { ...target, anchor: fallback };
      });
    });
  }, [copros]);

  const managerColors = useMemo(() => {
    const mapping = new Map<number, string>();
    targets.forEach((target, index) => {
      mapping.set(target.user.id, MARKER_COLORS[index % MARKER_COLORS.length]);
    });
    return mapping;
  }, [targets]);

  const managerMarkers = useMemo<SmartAttributionManagerMarker[]>(() => targets.map((target, index) => {
    const anchor = hasCoordinate(target.anchor) ? target.anchor : FRANCE_CENTER;
    return {
      userId: target.user.id,
      userName: renderUserLabel(target.user),
      lat: anchor.lat,
      long: anchor.long,
      color: MARKER_COLORS[index % MARKER_COLORS.length],
    draggable: true,
    };
  }), [targets]);

  const assignmentLookup = useMemo(() => {
    if (!result) {
      return new Map<number, number>();
    }
    const map = new Map<number, number>();
    result.clusters.forEach((cluster) => {
      cluster.assignments.forEach((assignment) => {
        map.set(assignment.coproId, cluster.userId);
      });
    });
    return map;
  }, [result]);

  const filteredCopros = useMemo(() => {
    if (!excludeLowSales) {
      return copros;
    }
    return copros.filter((copro) => (copro.salesCount ?? 0) >= MIN_SALES_THRESHOLD);
  }, [copros, excludeLowSales]);

  const hasCoprosWithSales = useMemo(() =>
    salesEntries.some((entry) => (entry.salesCount ?? 0) > 0),
    [salesEntries],
  );


  const filteredSalesEntriesCount = useMemo(() => {
    if (!excludeLowSales) {
      return totalCoprosCount;
    }
    return salesEntries.filter((entry) => (entry.salesCount ?? 0) >= MIN_SALES_THRESHOLD).length;
  }, [salesEntries, excludeLowSales, totalCoprosCount]);

  const coproMarkers = useMemo<SmartAttributionCoproMarker[]>(() => {
    const markers = filteredCopros
      .filter(hasLatLng)
      .map((copro) => ({
        coproId: copro.id,
        coproName: copro.name,
        lat: copro.lat!,
        long: copro.long!,
        salesAmount: copro.salesAmount,
        assignedUserId: assignmentLookup.get(copro.id) ?? null,
      }));

    return markers;
  }, [filteredCopros, assignmentLookup]);

  const tableData = useMemo(() => {
    if (!result) {
      return [];
    }
    return result.clusters.map((cluster) => renderClusterRow(cluster, userLookup, result.totalSalesAmount));
  }, [result, userLookup]);

  const totalSalesPerTransactionnaire = useMemo(() => {
    if (!result) {
      return {};
    }
    return result.clusters.reduce<Record<number, number>>((acc, cluster) => {
      acc[cluster.userId] = cluster.assignedSalesAmount;
      return acc;
    }, {});
  }, [result]);

  const canProceedToPlacement = areTargetsValid(targets);
  const canCompute = canProceedToPlacement && targets.every((target) => hasCoordinate(target.anchor));
  const isBusy = isLoadingCopros || isComputing || isSubmitting;
  const shouldBlockUI = isLoadingCopros || isSubmitting;

  const handlePercentageChange = (userId: number, percentage: number) => {
    const nextValue = Number.isFinite(percentage) ? Math.max(0, percentage) : 0;
    setTargets((current) => current.map((target) => (target.user.id === userId
      ? { ...target, percentage: nextValue }
      : target)));
    setPlacementDirty(true);
    setResult(null);
  };

  const handleUserSelection = (selectedUserIds: number[]) => {
    setTargets((current) => {
      const selectedUsers = availableTransactionnaires.filter((candidate) => selectedUserIds.includes(candidate.id));
      const nextTargets = buildEvenDistribution(selectedUsers);
      return nextTargets.map((target) => {
        const existing = current.find((item) => item.user.id === target.user.id);
        return existing ? { ...existing, percentage: target.percentage } : target;
      });
    });
    setPlacementDirty(true);
    setResult(null);
  };

  const handleManagerMove = useCallback((userId: number, coordinate: { lat: number; long: number }) => {
    setTargets((current) => {
      let changed = false;
      const next = current.map((target) => {
        if (target.user.id !== userId) {
          return target;
        }
        const previous = target.anchor;
        if (previous && Math.abs(previous.lat - coordinate.lat) < 0.000001 && Math.abs(previous.long - coordinate.long) < 0.000001) {
          return target;
        }
        changed = true;
        return { ...target, anchor: coordinate };
      });

      if (changed) {
        setPlacementDirty(true);
      }
      return next;
    });
  }, []);

  const handleGoToPlacement = async() => {
    if (!canProceedToPlacement) {
      showToast('error', 'Les pourcentages doivent totaliser 100% et être strictement positifs.');
      console.error('[SmartAttributionDialog] cannot proceed to placement, invalid targets', targets);
      return;
    }

    const loaded = await ensureCoprosLoaded();
    if (!loaded) {
      console.error('[SmartAttributionDialog] cannot proceed to placement, copros failed to load');
      return;
    }

    setStep('placement');
    setHighlightedTransactionnaire(null);
    setComputationError(null);
  };

  const runComputation = async() => {
    if (filteredCopros.length === 0) {
      showToast('error', 'Aucune copropriété géolocalisée disponible pour le calcul.');
      console.warn('[SmartAttributionDialog] compute aborted: copros array is empty');
      return;
    }

    if (!canCompute) {
      showToast('error', 'Veuillez positionner chaque transactionnaire sur la carte avant de lancer le calcul.');
       console.warn('[SmartAttributionDialog] compute aborted: missing anchors', targets);
      return;
    }

    setIsComputing(true);
    setComputationError(null);

    try {
      const validTargets = targets.filter((target) => hasCoordinate(target.anchor));
      const computationResult = computeSmartAttribution({
        copros: filteredCopros,
        targets: validTargets.map(toTargetInput),
        allowAnchorsFallback: false,
        debugParams,
      });

      setResult(computationResult);
      setStep('result');
      setPlacementDirty(false);
      setHighlightedTransactionnaire(null);
    } catch (error) {
      console.error('[SmartAttribution] compute error', error);
      setComputationError('Impossible de calculer l’attribution intelligente. Veuillez réessayer.');
    } finally {
      setIsComputing(false);
    }
  };

  const handleApply = async() => {
    if (!result) {
      return;
    }

    if (placementDirty) {
      showToast('error', 'Vous avez déplacé un transactionnaire. Recalculez l’attribution avant de valider.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onApply(result.clusters);
      showToast('success', 'Attribution intelligente appliquée avec succès.');
      onClose();
    } catch (error) {
      console.error('[SmartAttribution] apply error', error);
      showToast('error', 'Une erreur est survenue lors de l’enregistrement des attributions.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfigStep = () => (
    <div className="space-y-8">
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          Sélectionnez les transactionnaires éligibles et saisissez l’objectif de chiffre d’affaires que chacun doit gérer. Le total doit être égal à 100%.
        </p>
        <p className="text-sm text-gray-600">
          Après l’attribution intelligente, chaque ligne reste éditable depuis le tableau ci-dessous.
        </p>
      </div>

      <div>
        <label className="mb-2 block font-semibold">Transactionnaires concernés</label>
        <SelectMe
          multiple
          closeOnSelect={false}
          value={targets.map((target) => ({ value: target.user.id, label: renderUserLabel(target.user) }))}
          onChange={(options) => {
            const arrayOptions = Array.isArray(options) ? options : [];
            const nextSelectedIds = arrayOptions.map((opt) => Number(opt.value)).filter((value) => !Number.isNaN(value));
            handleUserSelection(nextSelectedIds);
          }}
          options={availableTransactionnaires.map((user) => ({
            value: user.id,
            label: renderUserLabel(user),
          }))}
          placeholder="Sélectionner les transactionnaires"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {targets.map((target) => (
          <div key={target.user.id} className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="font-semibold text-gray-800">{renderUserLabel(target.user)}</p>
            <label className="block text-sm font-medium text-gray-600">Objectif de CA (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={target.percentage}
              onChange={(event) => handlePercentageChange(target.user.id, Number(event.target.value))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
              step="1"
            />
          </div>
        ))}
      </div>

      <details className="border-t pt-4">
        <summary className="mb-4 cursor-pointer font-semibold text-gray-700">Paramètres de débogage (avancé)</summary>
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Rayon terrestre (km)</label>
            <input
              type="number"
              step="0.01"
              value={debugParams.earthRadiusKm}
              onChange={(e) => setDebugParams(prev => ({ ...prev, earthRadiusKm: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Epsilon</label>
            <input
              type="number"
              step="0.000001"
              value={debugParams.epsilon}
              onChange={(e) => setDebugParams(prev => ({ ...prev, epsilon: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Tolérance de capacité</label>
            <input
              type="number"
              step="0.01"
              value={debugParams.capacityTolerance}
              onChange={(e) => setDebugParams(prev => ({ ...prev, capacityTolerance: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">K voisins (KNN)</label>
            <input
              type="number"
              step="1"
              min="1"
              max="10"
              value={debugParams.knnK}
              onChange={(e) => setDebugParams(prev => ({ ...prev, knnK: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Distance max KNN (km)</label>
            <input
              type="number"
              step="1"
              min="1"
              value={debugParams.knnMaxDistanceKm}
              onChange={(e) => setDebugParams(prev => ({ ...prev, knnMaxDistanceKm: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Poids distance KNN</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={debugParams.knnDistanceWeight}
              onChange={(e) => setDebugParams(prev => ({ ...prev, knnDistanceWeight: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Poids capacité KNN</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={debugParams.knnCapacityWeight}
              onChange={(e) => setDebugParams(prev => ({ ...prev, knnCapacityWeight: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Diviseur priorité distance</label>
            <input
              type="number"
              step="1"
              value={debugParams.distancePriorityDivider}
              onChange={(e) => setDebugParams(prev => ({ ...prev, distancePriorityDivider: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Max passes d'assignation</label>
            <input
              type="number"
              step="1"
              value={debugParams.maxAssignmentPasses}
              onChange={(e) => setDebugParams(prev => ({ ...prev, maxAssignmentPasses: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Max itérations rééquilibrage</label>
            <input
              type="number"
              step="1"
              value={debugParams.maxRebalanceIterations}
              onChange={(e) => setDebugParams(prev => ({ ...prev, maxRebalanceIterations: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Pénalité réassignation distance</label>
            <input
              type="number"
              step="0.01"
              value={debugParams.distanceReassignPenalty}
              onChange={(e) => setDebugParams(prev => ({ ...prev, distanceReassignPenalty: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Nombre de voisins (KNN)</label>
            <input
              type="number"
              min={1}
              step="1"
              value={debugParams.rebalanceNeighborCount}
              onChange={(e) => setDebugParams((prev) => ({ ...prev, rebalanceNeighborCount: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Distance max voisins (km)</label>
            <input
              type="number"
              step="0.1"
              value={debugParams.rebalanceMaxDistanceKm}
              onChange={(e) => setDebugParams((prev) => ({ ...prev, rebalanceMaxDistanceKm: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">Poids gain distance (KNN)</label>
            <input
              type="number"
              step="0.1"
              value={debugParams.rebalanceDistanceGainWeight}
              onChange={(e) => setDebugParams((prev) => ({ ...prev, rebalanceDistanceGainWeight: Number(e.target.value) }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDebugParams({ ...DEBUG_DEFAULTS })}
          className="text-sm text-rgpd-blue-light underline hover:text-rgpd-blue-dark"
        >
          Réinitialiser aux valeurs par défaut
        </button>
      </details>

      <div className="flex items-center justify-between border-t pt-4">
        <div>
          <p className="text-sm text-gray-600">
            Total assigné :
            {' '}
            <span className="font-semibold">{sumPercentages(targets)}%</span>
          </p>
          {sumPercentages(targets) !== 100 && (
            <p className="text-xs text-red-600">Le total des pourcentages doit être exactement égal à 100%.</p>
          )}
        </div>
        {hasCoprosWithSales && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-label={`Exclure les copros avec moins de ${MIN_SALES_THRESHOLD} ventes`}
              aria-checked={excludeLowSales}
              onClick={() => setExcludeLowSales((previous) => !previous)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${excludeLowSales ? 'bg-rgpd-blue-light' : 'bg-gray-300'}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${excludeLowSales ? 'translate-x-5' : 'translate-x-1'}`}
              />
            </button>
            <div className="flex flex-col text-right text-sm text-gray-700">
              <span>
                Exclure les copros {'< '}
                {MIN_SALES_THRESHOLD}
                {' '}
                ventes
              </span>
              <span className="text-xs text-gray-500">
                {filteredSalesEntriesCount}
                /
                {totalCoprosCount}
                {' '}
                éligibles pour l’attribution
              </span>
            </div>
          </div>
        )}
        <button
          onClick={handleGoToPlacement}
          disabled={!canProceedToPlacement || isBusy}
          className="rounded bg-rgpd-blue-light px-4 py-2 text-white disabled:opacity-50"
        >
          {isLoadingCopros ? 'Chargement…' : 'Positionner sur la carte'}
        </button>
      </div>
    </div>
  );

  const renderPlacementStep = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-semibold text-gray-800">Placez chaque transactionnaire au centre de sa zone de gestion</h3>
        <p className="text-sm text-gray-600">
          Faites glisser les marqueurs correspondants aux transactionnaires pour les positionner au centre de la zone qu’ils couvriront. Les copropriétés sont affichées en gris et seront réparties autour de ces points lors du calcul.
        </p>
        {loadError && <p className="text-sm text-red-600">{loadError}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="space-y-3 xl:col-span-1">
          {targets.map((target, index) => {
            const anchor = target.anchor ?? null;
            return (
              <div key={target.user.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: MARKER_COLORS[index % MARKER_COLORS.length], display: 'inline-block' }}
                    aria-hidden
                  />
                  <div>
                    <p className="font-semibold text-gray-800">{renderUserLabel(target.user)}</p>
                    <p className="text-xs text-gray-500">Objectif {target.percentage}%</p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Position :
                  {' '}
                  {anchor ? `${anchor.lat.toFixed(4)}°, ${anchor.long.toFixed(4)}°` : 'à définir'}
                </p>
              </div>
            );
          })}
        </div>
        <div className="xl:col-span-3">
          <AttributionMap
            coproMarkers={coproMarkers}
            managerMarkers={managerMarkers}
            highlightedUserId={highlightedTransactionnaire}
            onHighlight={setHighlightedTransactionnaire}
            onManagerMove={handleManagerMove}
            readOnly={isBusy}
          />
        </div>
      </div>

      {computationError && <p className="text-sm text-red-600">{computationError}</p>}

      <div className="flex items-center justify-between border-t pt-4">
        <button
          className="rounded border border-gray-300 px-4 py-2 text-gray-700"
          onClick={() => {
            setStep('config');
            setResult(null);
            setComputationError(null);
          }}
          disabled={isBusy}
        >
          Retour aux objectifs
        </button>
        <button
          className="rounded bg-rgpd-blue-light px-4 py-2 text-white disabled:opacity-50"
          onClick={runComputation}
          disabled={!canCompute || isBusy}
        >
          {isComputing ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" aria-label="Calcul en cours" />
              Calcul en cours…
            </span>
          ) : 'Calculer l’attribution'}
        </button>
      </div>
    </div>
  );

  const renderResultStep = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Total CA théorique (3 dernières années) :
            {' '}
            <span className="font-semibold">{formatCurrency(result?.totalSalesAmount ?? 0)}</span>
          </p>
          <p className="text-xs text-gray-500">
            Chaque entrée peut ensuite être modifiée dans le tableau sur la même page.
          </p>
          {placementDirty && (
            <p className="mt-2 text-xs text-amber-600">
              Vous avez déplacé un transactionnaire après le calcul. Recalculez pour obtenir une proposition à jour.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded border border-gray-300 px-4 py-2 text-gray-700"
            onClick={() => {
              setStep('config');
              setResult(null);
              setPlacementDirty(true);
            }}
            disabled={isBusy}
          >
            Ajuster les paramètres
          </button>
          <button
            className="rounded border border-gray-300 px-4 py-2 text-gray-700"
            onClick={() => {
              setHighlightedTransactionnaire(null);
              setComputationError(null);
              setStep('placement');
            }}
            disabled={isBusy}
          >
            Repositionner sur la carte
          </button>
          <button
            className="rounded bg-rgpd-blue-light px-4 py-2 text-white disabled:opacity-50"
            onClick={runComputation}
            disabled={isBusy}
          >
            {isComputing ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" aria-label="Calcul en cours" />
                Calcul en cours…
              </span>
            ) : 'Recalculer'}
          </button>
          <button
            className="rounded bg-rgpd-blue-dark px-4 py-2 text-white disabled:opacity-50"
            onClick={handleApply}
            disabled={isSubmitting || placementDirty || !result}
          >
            {isSubmitting ? 'Attribution en cours…' : 'Valider la proposition'}
          </button>
        </div>
      </div>
      <div className="grid min-h-[420px] grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="order-2 flex flex-col space-y-4 xl:order-1">
          <div className="flex flex-wrap gap-3">
            {result?.clusters.map((cluster) => {
              const user = userLookup[cluster.userId];
              const color = managerColors.get(cluster.userId) ?? '#2563eb';
              const totalSales = totalSalesPerTransactionnaire[cluster.userId] ?? 0;
              return (
                <button
                  key={cluster.userId}
                  type="button"
                  onMouseEnter={() => setHighlightedTransactionnaire(cluster.userId)}
                  onMouseLeave={() => setHighlightedTransactionnaire(null)}
                  onFocus={() => setHighlightedTransactionnaire(cluster.userId)}
                  onBlur={() => setHighlightedTransactionnaire(null)}
                  className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 transition-colors ${highlightedTransactionnaire === cluster.userId ? 'border-rgpd-blue-light bg-rgpd-blue-light/10' : 'border-gray-200 bg-white'}`}
                >
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: color, display: 'inline-block' }}
                    aria-hidden
                  />
                  <span className="text-left">
                    <span className="block font-semibold">{user ? renderUserLabel(user) : 'Utilisateur inconnu'}</span>
                    <span className="text-xs text-gray-600">
                      Objectif {cluster.targetPercentage}% • Attribution {percentageFormatter.format(cluster.coveragePercentage)}% • {cluster.assignments.length} copros
                    </span>
                  </span>
                  <span className="ml-auto text-sm font-semibold">
                    {formatCurrency(totalSales)}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="min-h-[240px] grow">
            <Table
              data={tableData}
              headers={headers}
              idKey="id"
              tableClass="min-w-full bg-white border text-sm"
            />
          </div>
        </div>
        <div className="order-1 flex xl:order-2">
          <AttributionMap
            coproMarkers={coproMarkers}
            managerMarkers={managerMarkers}
            highlightedUserId={highlightedTransactionnaire}
            onHighlight={setHighlightedTransactionnaire}
            onManagerMove={handleManagerMove}
            readOnly={isBusy}
          />
        </div>
      </div>
      {computationError && <p className="text-sm text-red-600">{computationError}</p>}
    </div>
  );

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => {
        if (isBusy) {
          return;
        }
        onClose();
      }}
      title="Attribution par localisation et ÇA cible"
      additionalClass="w-full max-w-5xl h-[90vh]"
      disableCloseButton={isSubmitting}
    >
      <div className="flex h-full flex-col">
        {shouldBlockUI && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
            <div className="flex flex-col items-center gap-3">
              <Spinner aria-label="Chargement" size="xl" />
              <p className="text-sm font-medium text-gray-600">
                {isSubmitting ? 'Attribution en cours…' : 'Chargement des données…'}
              </p>
            </div>
          </div>
        )}
        <div className="grow space-y-6 overflow-y-auto pr-2">
          {step === 'config' && renderConfigStep()}
          {step === 'placement' && renderPlacementStep()}
          {step === 'result' && renderResultStep()}
        </div>
      </div>
    </Popup>
  );
};

export default SmartAttributionDialog;


